import { useState, useEffect, useRef } from 'react';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const isTransparent = !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleCategoryClick = (url: string) => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    window.open(url, '_blank', 'noopener,nofollow');
  };

  const handleMainSiteClick = () => {
    window.open('https://kaspreadsheet.com/', '_blank', 'noopener,nofollow');
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const textColor = isTransparent ? 'text-background-50' : 'text-foreground-950';
  const textMuted = isTransparent ? 'text-background-50/70' : 'text-foreground-700';
  const textHover = isTransparent ? 'hover:text-background-100' : 'hover:text-foreground-950';
  const bgHover = isTransparent ? 'hover:bg-background-50/10' : 'hover:bg-background-100';
  const dropdownBtn = isTransparent
    ? 'bg-background-50/10 border-background-50/20 text-background-50 hover:bg-background-50/20'
    : 'bg-background-100 border-background-200/70 text-foreground-800 hover:bg-background-200';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-10">
          <div className="flex items-center justify-between h-16 md:h-18">
            <a
              href="/"
              className={`flex items-center gap-2 no-underline ${textColor} transition-colors duration-300`}
            >
              <span className="text-lg md:text-xl font-bold font-heading tracking-tight">
                CSSBuy <span className="font-light italic">Spreadsheet</span>
              </span>
            </a>

            <div className="flex items-center gap-2 md:gap-3">
              <div className="hidden md:flex items-center gap-1">
                <a
                  href="/"
                  className={`px-3 py-2 text-sm transition-colors whitespace-nowrap rounded-lg ${textMuted} ${textHover} ${bgHover}`}
                >
                  Home
                </a>
                <a
                  href="#categorias"
                  className={`px-3 py-2 text-sm transition-colors whitespace-nowrap rounded-lg ${textMuted} ${textHover} ${bgHover}`}
                >
                  Guia de Compras
                </a>
                <a
                  href="/artigo/cssbuy-spreadsheet-guia-completo"
                  className={`px-3 py-2 text-sm transition-colors whitespace-nowrap rounded-lg ${textMuted} ${textHover} ${bgHover}`}
                >
                  Guia Completo
                </a>
                <a
                  href="#artigos"
                  className={`px-3 py-2 text-sm transition-colors whitespace-nowrap rounded-lg ${textMuted} ${textHover} ${bgHover}`}
                >
                  Artigos
                </a>
                <a
                  href="#faq"
                  className={`px-3 py-2 text-sm transition-colors whitespace-nowrap rounded-lg ${textMuted} ${textHover} ${bgHover}`}
                >
                  FAQ
                </a>
              </div>

              <div className="relative hidden md:block" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={`flex items-center gap-2 px-4 py-2.5 border rounded-full text-sm transition-all cursor-pointer whitespace-nowrap ${dropdownBtn}`}
                >
                  <i className="ri-store-2-line text-base w-4 h-4 flex items-center justify-center"></i>
                  <span>Categorias</span>
                  <i
                    className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                      dropdownOpen ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-background-50 border border-background-200/70 rounded-xl shadow-sm overflow-hidden z-50 max-h-[420px] overflow-y-auto">
                    <div className="p-2">
                      {categories.map((cat) => (
                        <button
                          key={cat.slug}
                          onClick={() => handleCategoryClick(cat.url)}
                          className="w-full text-left px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100 rounded-lg transition-colors flex items-center justify-between group cursor-pointer whitespace-nowrap"
                        >
                          <span>{cat.name}</span>
                          <i className="ri-arrow-right-up-line text-xs text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={handleMainSiteClick}
                className="hidden md:flex items-center gap-1.5 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
              >
                Buy It Now
                <i className="ri-arrow-right-up-line text-sm"></i>
              </button>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`md:hidden flex items-center justify-center w-10 h-10 rounded-lg transition-colors cursor-pointer ${
                  isTransparent
                    ? 'text-background-50 hover:bg-background-50/10'
                    : 'text-foreground-950 hover:bg-background-100'
                }`}
                aria-label="Menu"
              >
                <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={closeMobileMenu}
        />
      )}

      <div
        ref={mobileMenuRef}
        className={`fixed top-0 right-0 z-50 h-full w-[300px] max-w-[85vw] bg-background-50 shadow-lg transform transition-transform duration-300 ease-out md:hidden overflow-y-auto ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-4 border-b border-background-200/70">
          <span className="text-base font-bold font-heading text-foreground-950">
            CSSBuy <span className="font-light italic">Spreadsheet</span>
          </span>
          <button
            onClick={closeMobileMenu}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-4">
          <div className="mb-6">
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">
              Navegação
            </p>
            <div className="space-y-1">
              <a
                href="/"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-800 hover:bg-background-100 transition-colors"
              >
                <i className="ri-home-4-line text-base w-5 h-5 flex items-center justify-center text-primary-500"></i>
                Home
              </a>
              <a
                href="#categorias"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-800 hover:bg-background-100 transition-colors"
              >
                <i className="ri-shopping-bag-3-line text-base w-5 h-5 flex items-center justify-center text-primary-500"></i>
                Guia de Compras
              </a>
              <a
                href="/artigo/cssbuy-spreadsheet-guia-completo"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-800 hover:bg-background-100 transition-colors"
              >
                <i className="ri-book-open-line text-base w-5 h-5 flex items-center justify-center text-primary-500"></i>
                Guia Completo
              </a>
              <a
                href="#artigos"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-800 hover:bg-background-100 transition-colors"
              >
                <i className="ri-article-line text-base w-5 h-5 flex items-center justify-center text-primary-500"></i>
                Artigos
              </a>
              <a
                href="#faq"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-800 hover:bg-background-100 transition-colors"
              >
                <i className="ri-question-answer-line text-base w-5 h-5 flex items-center justify-center text-primary-500"></i>
                FAQ
              </a>
            </div>
          </div>

          <div className="mb-6">
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">
              Categorias
            </p>
            <div className="space-y-0.5">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryClick(cat.url)}
                  className="w-full text-left flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span>{cat.name}</span>
                  <i className="ri-arrow-right-up-line text-xs text-foreground-400"></i>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={() => { handleMainSiteClick(); closeMobileMenu(); }}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-semibold transition-all cursor-pointer"
          >
            Buy It Now
            <i className="ri-arrow-right-up-line text-base"></i>
          </button>
        </div>
      </div>
    </>
  );
}